import { useEffect, useId, useRef, useState } from 'react';
import type { BbgUsageDay, BbgUsageMetric } from '@shared/contract';
import {
  METRIC_LABEL,
  average,
  count,
  dayLabel,
  dayValue,
  niceScale,
  tickLabel,
  unknownRuns,
} from '@/lib/bbgUsage';

interface Props {
  terminalId: string;
  metric: BbgUsageMetric;
  days: BbgUsageDay[];
  todayDate: string | null;
  baseline: number | null;
  threshold: number | null;
  surging: boolean;
}

const HEIGHT = 176;
const MARGIN = { top: 10, right: 76, bottom: 22, left: 40 };
const DEFAULT_WIDTH = 640;
const MAX_BAR = 24;
const BAR_GAP = 2;
const LABEL_GAP = 12;

/** Width of the chart's box, so geometry is drawn at 1:1 rather than scaled text. */
function useElementWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      if (el.clientWidth > 0) setWidth(Math.round(el.clientWidth));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/** Column with a 4px rounded data-end and a square foot on the baseline. */
function barPath(x: number, top: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  const bottom = top + h;
  return (
    `M${x},${bottom}V${top + r}` +
    `A${r},${r} 0 0 1 ${x + r},${top}H${x + w - r}` +
    `A${r},${r} 0 0 1 ${x + w},${top + r}V${bottom}Z`
  );
}

export function HatchPattern({ id }: { id: string }) {
  return (
    <pattern
      id={id}
      width="6"
      height="6"
      patternUnits="userSpaceOnUse"
      patternTransform="rotate(45)"
    >
      <line className="uc-hatch" x1="0" y1="0" x2="0" y2="6" />
    </pattern>
  );
}

/**
 * Daily usage for one terminal: one column per day, today emphasised, the
 * baseline average and the surge threshold as reference lines. Days before
 * history began are hatched, so "unknown" never reads as "zero".
 */
export function UsageChart({
  terminalId,
  metric,
  days,
  todayDate,
  baseline,
  threshold,
  surging,
}: Props) {
  const { ref, width } = useElementWidth(DEFAULT_WIDTH);
  const [hover, setHover] = useState<number | null>(null);
  const hatchId = `uc-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const values = days.map((day) => dayValue(day, metric));
  const n = values.length;
  const found = todayDate ? days.findIndex((d) => d.date === todayDate) : -1;
  const todayIdx = found >= 0 ? found : n - 1;

  const known = values.filter((v): v is number => v != null);
  const scale = niceScale(Math.max(0, ...known, baseline ?? 0, threshold ?? 0));

  const plotW = Math.max(120, width - MARGIN.left - MARGIN.right);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const baseY = MARGIN.top + plotH;
  const right = MARGIN.left + plotW;
  const y = (v: number) => baseY - (v / scale.max) * plotH;
  const slot = n ? plotW / n : plotW;
  const barW = Math.min(MAX_BAR, Math.max(1, slot - BAR_GAP));
  const slotX = (i: number) => MARGIN.left + i * slot;

  // Reference-line labels sit in the right margin; when the two lines run
  // close, the threshold label (always the higher line) moves up.
  const avgY = baseline != null ? y(baseline) : null;
  const surgeY = threshold != null ? y(threshold) : null;
  let avgLabelY = avgY;
  let surgeLabelY = surgeY;
  if (avgLabelY != null && surgeLabelY != null && avgLabelY - surgeLabelY < LABEL_GAP) {
    surgeLabelY = Math.max(MARGIN.top, avgLabelY - LABEL_GAP);
    avgLabelY = Math.max(avgLabelY, surgeLabelY + LABEL_GAP);
  }

  // Date ticks: the first day, three evenly spaced, and today — dropping any
  // that would collide with today's label.
  const step = Math.max(1, Math.round(n / 4));
  const tickIdx: number[] = [];
  for (let i = 0; i < n; i += step) {
    if (todayIdx >= 0 && Math.abs(i - todayIdx) * slot < 52) continue;
    tickIdx.push(i);
  }
  if (todayIdx >= 0) tickIdx.push(todayIdx);

  const shown = hover ?? (todayIdx >= 0 ? todayIdx : null);
  const shownDay = shown != null ? days[shown] : undefined;
  const shownValue = shown != null ? values[shown] : null;

  return (
    <div className="usage-chart">
      <div className="usage-chart__head">
        <span className="usage-chart__title">
          {METRIC_LABEL[metric]} · {n}d
        </span>
        {shownDay && (
          <span className="usage-chart__readout" aria-live="polite">
            {dayLabel(shownDay.date)} · {shownValue == null ? 'no data' : count(shownValue)}
          </span>
        )}
      </div>
      <div ref={ref} className="usage-chart__plot">
        <svg
          viewBox={`0 0 ${MARGIN.left + plotW + MARGIN.right} ${HEIGHT}`}
          width={MARGIN.left + plotW + MARGIN.right}
          height={HEIGHT}
          role="img"
          aria-label={`${terminalId} ${METRIC_LABEL[metric].toLowerCase()} per day, last ${n} days`}
          onPointerLeave={() => setHover(null)}
        >
          <defs>
            <HatchPattern id={hatchId} />
          </defs>

          {scale.ticks.map((tick) => (
            <g key={tick}>
              <line className="uc-grid" x1={MARGIN.left} x2={right} y1={y(tick)} y2={y(tick)} />
              <text className="uc-tick" x={MARGIN.left - 6} y={y(tick)} textAnchor="end" dy="0.32em">
                {tickLabel(tick)}
              </text>
            </g>
          ))}

          {hover != null && (
            <rect
              className="uc-col"
              x={slotX(hover)}
              y={MARGIN.top}
              width={slot}
              height={plotH}
            />
          )}

          {unknownRuns(values).map(({ start, end }) => {
            const x = slotX(start);
            const w = (end - start + 1) * slot;
            return (
              <g key={`unknown-${start}`} className="uc-unknown">
                <rect x={x} y={MARGIN.top} width={w} height={plotH} fill={`url(#${hatchId})`} />
                {w >= 60 && (
                  <text className="uc-nodata" x={x + w / 2} y={MARGIN.top + plotH / 2} textAnchor="middle" dy="0.32em">
                    no data
                  </text>
                )}
              </g>
            );
          })}

          {values.map((value, i) => {
            if (value == null) return null;
            const x = slotX(i) + (slot - barW) / 2;
            const tone = i === todayIdx ? (surging ? ' is-surge' : ' is-today') : '';
            if (value <= 0) {
              return <rect key={i} className={`uc-bar is-zero${tone}`} x={x} y={baseY - 2} width={barW} height={2} />;
            }
            const h = Math.max(2, baseY - y(value));
            return <path key={i} className={`uc-bar${tone}`} d={barPath(x, baseY - h, barW, h)} />;
          })}

          <line className="uc-axis" x1={MARGIN.left} x2={right} y1={baseY} y2={baseY} />

          {avgY != null && avgLabelY != null && (
            <g>
              <line className="uc-avg" x1={MARGIN.left} x2={right} y1={avgY} y2={avgY} />
              <text className="uc-ref-label" x={right + 6} y={avgLabelY} dy="0.32em">
                avg {average(baseline)}
              </text>
            </g>
          )}
          {surgeY != null && surgeLabelY != null && (
            <g>
              <line className="uc-surge" x1={MARGIN.left} x2={right} y1={surgeY} y2={surgeY} />
              <text className="uc-ref-label" x={right + 6} y={surgeLabelY} dy="0.32em">
                surge {average(threshold)}
              </text>
            </g>
          )}

          {tickIdx.map((i) => {
            const day = days[i];
            if (!day) return null;
            const isToday = i === todayIdx;
            const cx = slotX(i) + slot / 2;
            const anchor = isToday ? 'end' : i === 0 ? 'start' : 'middle';
            const tx = isToday ? slotX(i) + slot : i === 0 ? slotX(0) : cx;
            return (
              <text
                key={`x-${i}`}
                className={`uc-tick${isToday ? ' is-today' : ''}`}
                x={tx}
                y={baseY + 15}
                textAnchor={anchor}
              >
                {dayLabel(day.date)}
              </text>
            );
          })}

          {values.map((_, i) => (
            <rect
              key={`hit-${i}`}
              className="uc-hit"
              x={slotX(i)}
              y={MARGIN.top}
              width={slot}
              height={plotH + MARGIN.bottom}
              onPointerEnter={() => setHover(i)}
            />
          ))}
        </svg>
      </div>
    </div>
  );
}
